
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import DailyBarChart from '@/components/shared/DailyBarChart.vue';
import DonutChart, { IDonutChartSlice } from '@/components/shared/DonutChart.vue';
import { CATEGORICAL_COLORS, NEUTRAL_SERIES_COLOR, OTHER_SERIES_COLOR } from '@/constants/chartColors';
import {
  auditLogService,
  IAuditLogAuthor,
  IAuditLogChart,
  IAuditLogCharts,
  IAuditLogDailyCharts,
  IAuditLogTopEditor,
} from '@/services/auditLogService';

export interface IAuditLogChartSelection {
  table_name: string;
  action: string;
  date: string;
}

interface IChartDefinition {
  key: keyof IAuditLogDailyCharts;
  icon: string;
  color: string;
}

const SERIES_BLUE = '#2a78d6';
const SERIES_RED = '#e34948';

// Categorical order of the validated reference palette; ranks 9 and 10 fall back to two neutral
// steps instead of generating hues (the ranked legend carries every name and value).
const EDITOR_COLORS = [...CATEGORICAL_COLORS, NEUTRAL_SERIES_COLOR, OTHER_SERIES_COLOR];

const CHART_DEFINITIONS: IChartDefinition[] = [
  { key: 'registered_users', icon: 'mdi-account-plus-outline', color: SERIES_BLUE },
  { key: 'deleted_users', icon: 'mdi-account-remove-outline', color: SERIES_RED },
  { key: 'created_service_providers', icon: 'mdi-store-plus-outline', color: SERIES_BLUE },
];

/**
 * Fixed daily views of the audit log, one per tab. Clicking a day emits the filters that list its rows.
 */
@Component({
  name: 'AuditLogCharts',
  components: { DailyBarChart, DonutChart },
})
export default class AuditLogCharts extends Vue {
  // The day currently listed in the table, to highlight its bar.
  @Prop({ default: null })
  protected selection!: IAuditLogChartSelection | null;

  // The author the table is filtered by, to highlight its slice.
  @Prop({ default: null })
  protected activeAuthorId!: number | null;

  readonly chartDefinitions: IChartDefinition[] = CHART_DEFINITIONS;

  protected tab: number = 0;
  protected loading: boolean = true;
  protected refreshing: boolean = false;
  protected charts: IAuditLogCharts | null = null;

  created() {
    this.load();
  }

  get current(): IChartDefinition {
    return this.chartDefinitions[this.tab] || this.chartDefinitions[0];
  }

  get currentChart(): IAuditLogChart | null {
    return this.charts ? this.charts[this.current.key] : null;
  }

  // generated_at is server time 'YYYY-MM-DD HH:mm:ss'
  get generatedTime(): string {
    return this.charts && this.charts.generated_at ? this.charts.generated_at.substring(11, 16) : '';
  }

  get generatedDate(): string {
    if (!this.charts || !this.charts.generated_at) {
      return '';
    }
    const [year, month, day] = this.charts.generated_at.substring(0, 10).split('-').map(Number);

    return new Date(year, month - 1, day).toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short' });
  }

  get editors(): IAuditLogTopEditor[] {
    return this.charts ? this.charts.top_editors.editors : [];
  }

  get editorSlices(): IDonutChartSlice[] {
    return this.editors.map((editor, index) => ({
      key: editor.user_id,
      label: [editor.first_name, editor.last_name].filter(part => !!part).join(' ').trim() || editor.email || '#' + editor.user_id,
      sublabel: editor.email,
      value: editor.count,
      color: EDITOR_COLORS[index % EDITOR_COLORS.length],
    }));
  }

  onSelectEditor(userId: number) {
    const editor = this.editors.find(each => each.user_id === userId);
    if (!editor) {
      return;
    }
    const author: IAuditLogAuthor = {
      id: editor.user_id,
      first_name: editor.first_name,
      last_name: editor.last_name,
      email: editor.email,
    };
    this.$emit('select-author', author);
  }

  activeDateFor(chart: IAuditLogChart): string | null {
    const selection = this.selection;
    if (!selection || selection.table_name !== chart.table_name || selection.action !== chart.action) {
      return null;
    }

    return selection.date;
  }

  onSelect(chart: IAuditLogChart, date: string) {
    this.$emit('select', { table_name: chart.table_name, action: chart.action, date } as IAuditLogChartSelection);
  }

  async load() {
    this.loading = true;
    try {
      this.charts = await auditLogService.charts();
    } finally {
      this.loading = false;
    }
  }

  // Keeps the current charts on screen (dimmed) while the fresh ones are computed.
  async refresh() {
    this.loading = true;
    this.refreshing = true;
    try {
      this.charts = await auditLogService.refreshCharts();
    } finally {
      this.loading = false;
      this.refreshing = false;
    }
  }
}
